/**
 * /messages/$conversationId/t/$parentId — Phase 4e thread overlay.
 *
 * Renders `<ThreadView>` to the right of the timeline (the parent's
 * `<Outlet/>`). Esc anywhere on the page closes the thread → navigate
 * back to `/messages/$conversationId`. The Esc handler installs a
 * document-level listener so it works even when focus is inside the
 * composer's contenteditable (TipTap swallows the React-bubbled key
 * event before it can reach a parent's `onKeyDown`, same pitfall as
 * the `<Modal>`).
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { ThreadView } from '@/features/chat/components/ThreadView'

export const Route = createFileRoute('/messages/$conversationId/t/$parentId')({
  component: ThreadRouteComponent,
})

function ThreadRouteComponent() {
  const { conversationId, parentId } = Route.useParams()
  const navigate = useNavigate()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Skip when a Modal is showing (focus trapped inside) or when the
      // event has already been handled by another stopPropagation chain.
      if (event.defaultPrevented) return
      // Skip when the active element is a form field — the field's own
      // Esc-to-cancel runs first. This mirrors the `<Modal>` approach.
      const active = document.activeElement
      const tag = active?.tagName ?? ''
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      navigate({ to: '/messages/$conversationId', params: { conversationId } })
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [conversationId, navigate])

  // `key` forces a remount on parentId change so ThreadView's
  // unread-snapshot ref resets between different threads.
  return <ThreadView key={parentId} conversationId={conversationId} parentMessageId={parentId} />
}
